// src/Pages/ExamSetup/GenerateExam.js
import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  MenuItem,
  Select,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Checkbox,
  FormControlLabel,
  Grid,
  Snackbar,
  Alert,
  Paper,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { useSchool } from "../../context/SchoolContext";
import HeaderPreview from "../../Pages/ExamSetup/HeaderPreview";
import { uploadQuestionPaper } from "../../api/PdfDetails";
import Examset from "../../Pages/ExamSetup/AddExamDetails";
import {
  getStandardsAndClasses,
  getSubjectsByClass,
  getPdfsBySubject,
} from "../../api/generateQsn";
import { Close, Download, Visibility } from "@mui/icons-material";
import PdfPreviewModal from "./PdfPreviewModal";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";
import { useExamDetails } from "../../context/ExamDetailsContext";

const GenerateExam = () => {
  const [step, setStep] = useState(0);
  const [standards, setStandards] = useState([]);

  const [standard, setStandard] = useState("");
  const [subject, setSubject] = useState("");
  const [subjects, setSubjects] = useState([]);
  console.log(subjects);
  const [pdfList, setPdfList] = useState([]);
  const [selectedPdfs, setSelectedPdfs] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [isDownloading, setIsDownloading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedStandardName, setSelectedStandardName] = useState("");
  const [selectedSubjectName, setSelectedSubjectName] = useState("");
  const standardSelectRef = useRef();
  const subjectSelectRef = useRef();

  const { schoolData } = useSchool();
  const currentSchool = schoolData?.data?.[0] || {};
  const {
    firstExamDetails,
    loading: examLoading,
    error: examError,
  } = useExamDetails();

  // Fetch standards
  useEffect(() => {
    const fetchStandards = async () => {
      try {
        setLoading(true);
        const res = await getStandardsAndClasses();
        if (Array.isArray(res)) setStandards(res);
        else if (res?.data && Array.isArray(res.data)) setStandards(res.data);
        else setStandards([]);
      } catch (err) {
        console.error("Error fetching standards:", err);
        setSnackbar({
          open: true,
          message: "Failed to fetch standards",
          severity: "error",
        });
        setStandards([]);
      } finally {
        setLoading(false);
      }
    };
    fetchStandards();
  }, []);

  // Helper function to find item by ID
  const findItemById = (id, items) => {
    if (!id || !items || !Array.isArray(items)) return null;
    return items.find(
      (item) => item.id === id || item._id === id || item.value === id
    );
  };

  // Standard change
  const handleStandardChange = async (e) => {
    const stdId = e.target.value;

    // Find the standard name from standards array
    const selectedStandard = findItemById(stdId, standards);

    const stdName = selectedStandard ? selectedStandard.class_name : "";

    setStandard(stdId);
    setSelectedStandardName(stdName);
    setSubject("");
    setSelectedSubjectName("");
    setPdfList([]);
    setSelectedPdfs([]);
    setStep(1);

    try {
      setLoading(true);
      const res = await getSubjectsByClass(stdId);
      if (Array.isArray(res)) setSubjects(res);
      else if (res?.data && Array.isArray(res.data)) setSubjects(res.data);
      else setSubjects([]);
    } catch (err) {
      console.error("Error fetching subjects:", err);
      setSubjects([]);
      setSnackbar({
        open: true,
        message: "Failed to fetch subjects",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Subject change
  const handleSubjectChange = async (e) => {
    const subjId = e.target.value;

    // Find the subject name from subjects array
    const selectedSubject = findItemById(subjId, subjects);
    const subjName = selectedSubject ? selectedSubject.subject_name : "";

    setSubject(subjId);
    setSelectedSubjectName(subjName);
    setPdfList([]);
    setSelectedPdfs([]);
    setStep(2);

    try {
      setLoading(true);
      const res = await getPdfsBySubject(subjId);
      if (Array.isArray(res)) setPdfList(res);
      else if (res?.data && Array.isArray(res.data)) setPdfList(res.data);
      else setPdfList([]);
    } catch (err) {
      console.error("Error fetching PDFs:", err);
      setPdfList([]);
      setSnackbar({
        open: true,
        message: "Failed to fetch PDFs",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Toggle PDF selection
  const handlePdfToggle = (pdf) => {
    setSelectedPdfs((prev) =>
      prev.some((p) => p.id === pdf.id)
        ? prev.filter((p) => p.id !== pdf.id)
        : [...prev, pdf]
    );
  };

  // Preview
  const handlePreview = () => {
    if (selectedPdfs.length === 0) {
      setSnackbar({
        open: true,
        message: "Please select at least one PDF",
        severity: "warning",
      });
      return;
    }
    setPreviewOpen(true);
  };

  // Download + Upload merged PDFs with header


  const handleDownload = async () => {
    if (selectedPdfs.length === 0) {
      setSnackbar({
        open: true,
        message: "Please select at least one PDF",
        severity: "warning",
      });
      return;
    }

    setIsDownloading(true);

    try {
      const finalPdf = await PDFDocument.create();

      // ✅ Register fontkit for custom fonts
      finalPdf.registerFontkit(fontkit);

      // 👉 Load default fonts
      const font = await finalPdf.embedFont(StandardFonts.Helvetica);
      const boldFont = await finalPdf.embedFont(StandardFonts.HelveticaBold);

      // 👉 Load custom AppleTea font (must be in public/fonts folder)
      let customAppleTeaFont = null;
      try {
        const fontBytes = await fetch("/font/AppleTea-z8R1a.ttf").then((res) =>
          res.arrayBuffer()
        );
        customAppleTeaFont = await finalPdf.embedFont(fontBytes);
      } catch (err) {
        console.warn("AppleTea font not found, fallback to boldFont", err);
      }

      // 👉 Load custom Arial font
      let customArialFont = null;
      try {
        const arialBytes = await fetch("/font/arial.ttf").then((res) =>
          res.arrayBuffer()
        );
        customArialFont = await finalPdf.embedFont(arialBytes);
      } catch (err) {
        console.warn("Arial font not found, fallback to font", err);
      }

      let isFirstPage = true;

      for (const pdf of selectedPdfs) {
        const pdfUrl = pdf.file || pdf.url || pdf.document_url;
        if (!pdfUrl) continue;

        const existingPdfBytes = await fetch(pdfUrl).then((res) =>
          res.arrayBuffer()
        );
        const pdfDoc = await PDFDocument.load(existingPdfBytes);
        const pages = await finalPdf.copyPages(pdfDoc, pdfDoc.getPageIndices());

        for (const page of pages) {
          const { width, height } = page.getSize();

          if (isFirstPage) {
            // 🏫 School logo
            if (currentSchool?.logo) {
              try {
                const logoBytes = await fetch(currentSchool.logo).then((res) =>
                  res.arrayBuffer()
                );
                let logoImage = currentSchool.logo.endsWith(".png")
                  ? await finalPdf.embedPng(logoBytes)
                  : await finalPdf.embedJpg(logoBytes);
                page.drawImage(logoImage, {
                  x: 53,
                  y: height - 99,
                  width: 70,
                  height: 70,
                });
              } catch (err) {
                console.error("Logo error:", err);
              }
            }

            // 🏫 School name with AppleTea font
            const schoolName = currentSchool?.school_name || "";
            const schoolNameFont = customAppleTeaFont || boldFont;
            const schoolNameWidth = schoolNameFont.widthOfTextAtSize(
              schoolName,
              22
            );

            page.drawText(schoolName, {
              x: width / 2 - schoolNameWidth / 2,
              y: height - 45,
              size: 22,
              font: schoolNameFont,
              color: rgb(0, 0, 0),
            });

            // Other details (Helvetica / Arial style)
            const seniorLine = "";
            const seniorWidth = font.widthOfTextAtSize(seniorLine, 12);
            page.drawText(seniorLine, {
              x: width / 2 - seniorWidth / 2,
              y: height - 60,
              size: 18,
              font: customArialFont,
            });

            const board = ` ${currentSchool?.affiliated_board || ""}, `;
            const boardWidth = font.widthOfTextAtSize(board, 11);
            page.drawText(board, {
              x: width / 2 - boardWidth / 1.8,
              y: height - 65,
              size: 12,
              font: customArialFont,
            });

            const aff = ` ${currentSchool?.affiliation_number || ""}`;
            const affWidth = font.widthOfTextAtSize(aff, 11);
            page.drawText(aff, {
              x: width / 2 - affWidth / 1.8,
              y: height - 80,
              size: 12,
              font: customArialFont,
            });

            // 📄 Exam type banner
            const examType =
              firstExamDetails?.test_paper_name ||
              currentSchool?.exam_type ||
              "";
            const examTextWidth = boldFont.widthOfTextAtSize(examType, 14);

            page.drawRectangle({
              x: width / 2 - examTextWidth / 2 - 6,
              y: height - 109,
              width: examTextWidth + 12,
              font: schoolNameFont,
              height: 24,
              color: rgb(1, 1, 1), // white background
            });

            page.drawText(examType, {
              x: width / 2 - examTextWidth / 2,
              y: height - 109,
              size: 14,
              font: schoolNameFont,
              color: rgb(0, 0.5, 0),
            });

            // 📄 Class, Marks, Subject, Time
            const lineHeight = 22; // spacing between lines
            const leftX = 57;
            const rightX = width - 155;
            const fontSize = 13;
            const extraBottomMargin = 23; // more space from bottom
            const subInfoY = height - 170 + extraBottomMargin;

            // Left column
            const leftTexts = [
              `Class: ${selectedStandardName || ""}`,
              `Marks: ${firstExamDetails?.full_marks || ""}`,
            ];

            leftTexts.forEach((text, index) => {
              page.drawText(text, {
                x: leftX,
                y: subInfoY - lineHeight * index,
                size: fontSize,
                font: customArialFont,
              });
            });

            // Right column
            const rightTexts = [
              `Subject: ${selectedSubjectName || ""}`,
              `Time: ${firstExamDetails?.time_allowed || ""}`,
            ];

            rightTexts.forEach((text, index) => {
              page.drawText(text, {
                x: rightX,
                y: subInfoY - lineHeight * index,
                size: fontSize,
                font: customArialFont,
              });
            });

            isFirstPage = false;
          }

          finalPdf.addPage(page);
        }
      }

      // Save and download
      const pdfBytes = await finalPdf.save();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const file = new File([blob], `Exam_${Date.now()}.pdf`, {
        type: "application/pdf",
      });

      // Upload the generated PDF
      for (const pdf of selectedPdfs) {
        try {
          await uploadQuestionPaper(pdf.id, file);
        } catch (err) {
          console.error("Upload error for PDF ID:", pdf.id, err);
        }
      }
      // Trigger local download
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.name;
      link.click();
      URL.revokeObjectURL(url);

      setSnackbar({
        open: true,
        message: "PDF generated successfully",
        severity: "success",
      });
    } catch (error) {
      console.error("❌ Error merging/uploading PDFs:", error);
      setSnackbar({
        open: true,
        message: "Failed to process PDFs",
        severity: "error",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "1200px",
        mx: "auto",
        mt: 1,
        p: 2,
        borderRadius: 2,
        boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
        bgcolor: "#fefcfb",
      }}
    >
      <Typography
        variant="h4"
        sx={{
          mb: 1,
          fontWeight: 300,
          // textAlign: "center",
          background: "linear-gradient(45deg, #1976d2, #42a5f5)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}
      >
        Create a New Test
      </Typography>

      {/* Header Preview */}
      <HeaderPreview school={currentSchool} />

      {/* Exam Details component */}
      <Examset />

      {/* Stepper */}
      <Stepper
        activeStep={step}
        alternativeLabel
        sx={{
          mb: 6,
          "& .MuiStepLabel-label": { fontSize: "0.95rem", fontWeight: 600 },
          "& .MuiStepIcon-root.Mui-active": { color: "#1976d2" },
          "& .MuiStepIcon-root.Mui-completed": { color: "#4caf50" },
        }}
      >
        {["Select Standard", "Select Subject", "Choose PDFs"].map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", my: 6 }}>
          <CircularProgress size={40} />
        </Box>
      )}

      <Grid container spacing={4}>
        {/* Standard */}
        {step >= 0 && (
          <Grid item xs={12} sm={6} md={4}>
            <Typography
              variant="subtitle1"
              sx={{ mb: 1.5, fontWeight: 600, color: "text.primary" }}
            >
              Select Standard
            </Typography>
            <Select
              fullWidth
              value={standard}
              onChange={handleStandardChange}
              displayEmpty
              size="medium"
              ref={standardSelectRef}
              sx={{
                borderRadius: 3,
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#ccc" },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#1976d2",
                },
              }}
            >
              <MenuItem value="" disabled>
                Choose Standard
              </MenuItem>
              {standards.map((std) => (
                <MenuItem key={std.id} value={std.id}>
                  {std.name || std.class_name}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        )}

        {/* Subject */}
        {step >= 1 && standard && (
          <Grid item xs={12} sm={6} md={4}>
            <Typography
              variant="subtitle1"
              sx={{ mb: 1.5, fontWeight: 600, color: "text.primary" }}
            >
              Select Subject
            </Typography>
            <Select
              fullWidth
              value={subject}
              onChange={handleSubjectChange}
              displayEmpty
              size="medium"
              ref={subjectSelectRef}
              sx={{
                borderRadius: 3,
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#ccc" },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#1976d2",
                },
              }}
            >
              <MenuItem value="" disabled>
                Choose Subject
              </MenuItem>
              {subjects.map((subj) => (
                <MenuItem key={subj.id} value={subj.id}>
                  {subj.name || subj.subject_name}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        )}

        {/* PDFs */}
        {step >= 2 && (
          <Grid item xs={12}>
            <Typography
              variant="h6"
              sx={{ mb: 3, fontWeight: 700, color: "primary.main" }}
            >
              Select Question Papers
            </Typography>
            {pdfList.length > 0 ? (
              <Box>
                {pdfList.map((pdf) => (
                  <FormControlLabel
                    key={pdf.id}
                    control={
                      <Checkbox
                        checked={selectedPdfs.some((p) => p.id === pdf.id)}
                        onChange={() => handlePdfToggle(pdf)}
                        color="primary"
                      />
                    }
                    label={
                      <Box display="flex" alignItems="center">
                        <Typography fontWeight={500}>
                          {pdf.file || pdf.name}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedPdfs([pdf]);
                            setPreviewOpen(true);
                          }}
                          sx={{
                            ml: 1,
                            color: "primary.main",
                            "&:hover": { bgcolor: "rgba(25,118,210,0.1)" },
                          }}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Box>
                    }
                    sx={{
                      display: "block",
                      mb: 1.5,
                      px: 2,
                      py: 1.5,
                      borderRadius: 3,
                      transition: "all 0.3s ease",
                      bgcolor: selectedPdfs.some((p) => p.id === pdf.id)
                        ? "rgba(25,118,210,0.08)"
                        : "#fafafa",
                      "&:hover": {
                        bgcolor: "rgba(25,118,210,0.15)",
                        transform: "scale(1.01)",
                      },
                    }}
                  />
                ))}
                <Box sx={{ mt: 4, display: "flex", gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={handlePreview}
                    disabled={selectedPdfs.length === 0 || isDownloading}
                    startIcon={<Visibility />}
                    sx={{
                      borderRadius: 3,
                      px: 3,
                      py: 1,
                      fontWeight: 600,
                      textTransform: "none",
                      "&:hover": { bgcolor: "rgba(25,118,210,0.1)" },
                    }}
                  >
                    Preview Selected
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleDownload}
                    disabled={selectedPdfs.length === 0 || isDownloading}
                    startIcon={isDownloading ? <span>⏳</span> : <Download />}
                    sx={{
                      borderRadius: 3,
                      px: 3,
                      py: 1,
                      fontWeight: 600,
                      textTransform: "none",
                      boxShadow: "0 4px 12px rgba(25,118,210,0.3)",
                      "&:hover": {
                        boxShadow: "0 6px 18px rgba(25,118,210,0.4)",
                      },
                    }}
                  >
                    {isDownloading ? "Processing..." : "Download PDF(s)"}
                  </Button>
                </Box>
              </Box>
            ) : (
              !loading && (
                <Paper
                  sx={{
                    p: 4,
                    textAlign: "center",
                    bgcolor: "#fafafa",
                    borderRadius: 3,
                  }}
                >
                  <Typography variant="body1" color="text.secondary">
                    No question papers available for this subject.
                  </Typography>
                </Paper>
              )
            )}
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default GenerateExam;
